import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { Custom } from '@app/shared';
import {
  BaseControl,
  Textbox,
  Dropdown,
  EspacioControl,
  ColumnaMaestra,
  TablaMaestra,
  DateTimePicker,
  Container
} from '@app/shared';


@Injectable()
export class MasterStructureFormService {
  constructor() { }

  /**
   * Setea la data del combo de los tipos de tamaños de los controles
   *
   * @param {Array<EspacioControl>} sizesControl
   * @returns {Array<any>}
   * @memberof CreationFormsFormService
   */
  setSizes(sizesControl: Array<EspacioControl>): Array<any> {
    const sizes: Array<any> = [];
    sizesControl.forEach(size => {
      sizes.push({
        key: size.espacio,
        value: size.nombre
      });
    });
    return sizes;
  }

  /**
   * setea la data para el combo de lista de maestras para los controles
   *
   * @param {Array<TablaMaestra>} masterTables
   * @returns {Array<any>}
   * @memberof CreationFormsFormService
   */
  setMasterTables(masterTables: Array<TablaMaestra>): Array<any> {
    const masterT: Array<any> = [];
    masterTables.forEach(mt => {
      masterT.push({
        key: mt.id,
        value: mt.nombre
      });
    });
    return masterT;
  }

  /**
   * Crea el formulario para la creacion de los controles de tipo input
   *
   * @param {Array<EspacioControl>} sizesControl
   * @param {Control} [control]
   * @returns {BaseControl<any>[]}
   * @memberof CreationFormsFormService
   */
  getFormText(sizesControl: Array<EspacioControl>, control?: ColumnaMaestra): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'txtAlias',
        label: 'Alias del control:',
        value: control ? control.alias : '',
        describe: 'Nota: este alias se utiliza como referencia para poder acceder a cada campo del formulario ' +
          'y no debe llevar espacios ni caracteres especiales.',
        order: 1,
        placeholder: 'Alias del control',
        validations: {
          validators: [Validators.required, Validators.pattern('^[a-zA-Z_]*$')],
          messages: { pattern: 'Caracteres invalidos: solo letras y guiones bajos' }
        }
      }),
      new Dropdown({
        key: 'cboEspacios',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 4,
        options: this.setSizes(sizesControl),
        order: 2,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título del control:',
        value: control ? control.nombre : '',
        order: 3,
        placeholder: 'Título del control',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtPlaceholder',
        label: 'Placeholder del control:',
        value: control ? control.placeholder : '',
        order: 4,
        placeholder: 'Placeholder del control',
      }),
      new Textbox({
        key: "txtTooltip",
        label: "Tooltip del control:",
        value: control ? control.tooltip : "",
        order: 5,
        placeholder: "Tooltip del control",
      }),
      new Textbox({
        key: 'txtMin',
        label: 'Caracteres mínimos:',
        value: control ? control.propiedades['min'] ? control.propiedades['min'] : null : null,
        type: 'number',
        order: 6,
        placeholder: 'Caracteres mínimos',
      }),
      new Textbox({
        key: 'txtMax',
        label: 'Caracteres máximos:',
        value: control ? control.propiedades['max'] ? control.propiedades['max'] : null : null,
        type: 'number',
        order: 7,
        placeholder: 'Caracteres máximos',
      }),
      new Dropdown({
        key: 'cboTransform',
        label: 'Transformar texto:',
        value: control ?
          control.propiedades ?
            control.propiedades['transform'] ?
              control.propiedades['transform']
              : 'ninguna'
            : 'ninguna'
          : 'ninguna',
        options: [
          { key: 'ninguna', value: 'Ninguna transformación' },
          { key: 'uppercase', value: 'Mayúscula sostenida' },
          { key: 'lowercase', value: 'Minúscula sostenida' }
        ],
        order: 8,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtExpresion',
        label: 'Expresion regular:',
        value: control ? control.propiedades['expresion'] ? control.propiedades['expresion'] : '' : '',
        order: 9,
        placeholder: 'Expresion regular',
      }),
      new Textbox({
        key: 'txtErrorExp',
        label: 'Mensaje de error:',
        value: control ? control.propiedades['mensajeExpresion'] ? control.propiedades['mensajeExpresion'] : '' : '',
        order: 10,
        placeholder: 'Mensaje de error de la expresion regular',
      }),
      new Container({
        class: 'form-row',
        order: 11,
        controls: [
          new Textbox({
            key: 'chRequiered',
            type: 'checkbox',
            label: 'Requerido',
            size: 'col-12 col-md-4',
            value: control ? control.requerido : false,
          }),
          new Textbox({
            key: 'chOnly',
            type: 'checkbox',
            label: 'Unico',
            size: 'col-12 col-md-4',
            value: control ? control.unico : false,
          }),
        ]
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

  /**
   * Crea los campos del formulario para la creacion del control de area de texto
   *
   * @param {Array<EspacioControl>} sizesControl
   * @param {Control} [control]
   * @returns {BaseControl<any>[]}
   * @memberof CreationFormsFormService
   */
  getFormTextArea(sizesControl: Array<EspacioControl>, control?: ColumnaMaestra): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'txtAlias',
        label: 'Alias del control:',
        value: control ? control.alias : '',
        describe: 'Nota: este alias se utiliza como referencia para poder acceder a cada campo del formulario ' +
          'y no debe llevar espacios ni caracteres especiales.',
        order: 1,
        placeholder: 'Alias del control',
        validations: {
          validators: [Validators.required, Validators.pattern('^[a-zA-Z_]*$')],
          messages: { pattern: 'Caracteres invalidos: solo letras y guiones bajos' }
        }
      }),
      new Dropdown({
        key: 'cboEspacios',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 4,
        options: this.setSizes(sizesControl),
        order: 2,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título del control:',
        value: control ? control.nombre : '',
        order: 3,
        placeholder: 'Título del control',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtPlaceholder',
        label: 'Placeholder del control:',
        value: control ? control.placeholder : '',
        order: 4,
        placeholder: 'Placeholder del control',
      }),
      new Textbox({
        key: "txtTooltip",
        label: "Tooltip del control:",
        value: control ? control.tooltip : "",
        order: 5,
        placeholder: "Tooltip del control",
      }),
      new Textbox({
        key: 'txtRows',
        label: 'Cantidad de líneas:',
        value: control ? control.propiedades['rows'] ? control.propiedades['rows'] : 1 : 1,
        type: 'number',
        order: 6,
        placeholder: 'Cantidad de líneas',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtMin',
        label: 'Caracteres mínimos:',
        value: control ? control.propiedades['min'] ? control.propiedades['min'] : null : null,
        type: 'number',
        order: 7,
        placeholder: 'Caracteres mínimos',
      }),
      new Textbox({
        key: 'txtMax',
        label: 'Caracteres máximos:',
        value: control ? control.propiedades['max'] ? control.propiedades['max'] : null : null,
        type: 'number',
        order: 8,
        placeholder: 'Caracteres máximos',
      }),
      new Dropdown({
        key: 'cboTransform',
        label: 'Transformar texto:',
        value: control ?
          control.propiedades ?
            control.propiedades['transform'] ?
              control.propiedades['transform']
              : 'ninguna'
            : 'ninguna'
          : 'ninguna',
        options: [
          { key: 'ninguna', value: 'Ninguna transformación' },
          { key: 'uppercase', value: 'Mayúscula sostenida' },
          { key: 'lowercase', value: 'Minúscula sostenida' }
        ],
        order: 9,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Container({
        class: 'form-row',
        order: 10,
        controls: [
          new Textbox({
            key: 'chRequiered',
            type: 'checkbox',
            label: 'Requerido',
            size: 'col-12 col-md-4',
            value: control ? control.requerido : false,
          }),
          new Textbox({
            key: 'chOnly',
            type: 'checkbox',
            label: 'Unico',
            size: 'col-12 col-md-4',
            value: control ? control.unico : false,
          }),
        ]
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

  /**
   * Crea el formulario de creacion para los controles tipo Check
   *
   * @param {Control} [control]
   * @returns {BaseControl<any>[]}
   * @memberof CreationFormsFormService
   */
  getFormCheck(control?: ColumnaMaestra): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'txtAlias',
        label: 'Alias del control:',
        value: control ? control.alias : '',
        describe: 'Nota: este alias se utiliza como referencia para poder acceder a cada campo del formulario ' +
          'y no debe llevar espacios ni caracteres especiales.',
        order: 1,
        placeholder: 'Alias del control',
        validations: {
          validators: [Validators.required, Validators.pattern('^[a-zA-Z_]*$')],
          messages: { pattern: 'Caracteres invalidos: solo letras y guiones bajos' }
        }
      }),
      new Textbox({
        key: 'numFilas',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 2,
        order: 2,
        type: 'number',
        validations: {
          validators: [Validators.required, Validators.min(2), Validators.max(12)],
          messages: {
            min: 'El valor minimo para este campo es de 2',
            max: 'El valor maximo para este campo es de 12'
          }
        },
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título del control:',
        value: control ? control.nombre : '',
        order: 3,
        placeholder: 'Título del control',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: "txtTooltip",
        label: "Tooltip del control:",
        value: control ? control.tooltip : "",
        order: 4,
        placeholder: "Tooltip del control",
      }),
      new Dropdown({
        key: 'cboDefault',
        label: 'Valor por defecto:',
        value: control ?
          control.propiedades['default'] ?
            control.propiedades['default']
            : 'noSeleccionado'
          : 'noSeleccionado',
        options: [
          { key: 'seleccionado', value: 'Seleccionado' },
          { key: 'noSeleccionado', value: 'No seleccionado' },
        ],
        order: 5,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Container({
        class: 'form-row',
        order: 6,
        controls: [
          new Textbox({
            key: 'chRequiered',
            type: 'checkbox',
            label: 'Requerido',
            size: 'col-12 col-md-4',
            value: control ? control.requerido : false,
          }),
          new Textbox({
            key: 'chOnly',
            type: 'checkbox',
            label: 'Unico',
            size: 'col-12 col-md-4',
            value: control ? control.unico : false,
          }),
        ]
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

  /**
   * Crea el formulario de creacion para los controles tipo Date
   *
   * @param {Array<EspacioControl>} sizesControl
   * @param {Control} [control]
   * @returns {BaseControl<any>[]}
   * @memberof CreationFormsFormService
   */
  getFormDate(sizesControl: Array<EspacioControl>, control?: ColumnaMaestra): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'txtAlias',
        label: 'Alias del control:',
        value: control ? control.alias : '',
        describe: 'Nota: este alias se utiliza como referencia para poder acceder a cada campo del formulario ' +
          'y no debe llevar espacios ni caracteres especiales.',
        order: 1,
        placeholder: 'Alias del control',
        validations: {
          validators: [Validators.required, Validators.pattern('^[a-zA-Z_]*$')],
          messages: { pattern: 'Caracteres invalidos: solo letras y guiones bajos' }
        }
      }),
      new Dropdown({
        key: 'cboEspacios',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 4,
        options: this.setSizes(sizesControl),
        order: 2,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título del control:',
        value: control ? control.nombre : '',
        order: 3,
        placeholder: 'Título del control',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: "txtTooltip",
        label: "Tooltip del control:",
        value: control ? control.tooltip : "",
        order: 4,
        placeholder: "Tooltip del control",
      }),
      new Dropdown({
        key: 'cboTypePicker',
        label: 'Tipo:',
        value: control ? control.propiedades['tipo'] ? control.propiedades['tipo'] : 'calendar' : 'calendar',
        options: [
          { key: 'calendar', value: 'Fecha' },
          { key: 'timer', value: 'Hora' },
          { key: 'dateTimer', value: 'Fecha y hora' },
          // { key: 'rangeDate', value: 'Rango de fecha' },
          // { key: 'rangeTime', value: 'Rango de tiempo' },
          // { key: 'range', value: 'Rango de fecha y hora' },
        ],
        order: 5,
        validations: {
          validators: [Validators.required]
        }
      }),
      new DateTimePicker({
        key: 'txtDesde',
        label: 'Fecha desde:',
        pickerType: 'calendar',
        value: control ? control.propiedades['fechaDesde'] ? control.propiedades['fechaDesde'] : null : null,
        order: 6,
        placeholder: 'Seleccionar fecha desde donde se seleccionara',
        condictions: [
          {
            campoDepende: 'cboTypePicker',
            siEs: 'Igual',
            valor: 'timer',
            accion: 'Ocultar'
          }
        ],
      }),
      new DateTimePicker({
        key: 'txtHasta',
        label: 'Fecha hasta:',
        pickerType: 'calendar',
        value: control ? control.propiedades['fechaHasta'] ? control.propiedades['fechaHasta'] : null : null,
        order: 7,
        placeholder: 'Seleccionar fecha hasta donde se seleccionara',
        condictions: [
          {
            campoDepende: 'cboTypePicker',
            siEs: 'Igual',
            valor: 'timer',
            accion: 'Ocultar'
          }
        ],
      }),
      new Textbox({
        key: 'txtHabiles',
        label: 'Días hábiles:',
        value: control ? control.propiedades['habiles'] ? control.propiedades['habiles'] : null : null,
        type: 'number',
        order: 8,
        placeholder: 'Días hábiles',
      }),
      new Container({
        class: 'form-row',
        order: 9,
        controls: [
          new Textbox({
            key: 'chRequiered',
            type: 'checkbox',
            label: 'Requerido',
            size: 'col-12 col-md-4',
            value: control ? control.requerido : false,
          }),
          new Textbox({
            key: 'chOnly',
            type: 'checkbox',
            label: 'Unico',
            size: 'col-12 col-md-4',
            value: control ? control.unico : false,
          }),
        ]
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

  /**
   * Crea el formulario de creacion para los controles tipo Select con filtro
   *
   * @param {Array<EspacioControl>} sizesControl
   * @param {Array<TablaMaestra>} masterTables
   * @param {*} templeteForm
   * @param {ColumnaMaestra} [control]
   * @param {Array<any>} [optionsSelectColum]
   * @returns {BaseControl<any>[]}
   * @memberof MasterStructureFormService
   */
  getFormSelect(
    sizesControl: Array<EspacioControl>,
    masterTables: Array<TablaMaestra>,
    templeteForm: any,
    control?: ColumnaMaestra,
    optionsSelectColum?: Array<any>
  ): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'txtAlias',
        label: 'Alias del control:',
        value: control ? control.alias : '',
        describe: 'Nota: este alias se utiliza como referencia para poder acceder a cada campo del formulario ' +
          'y no debe llevar espacios ni caracteres especiales.',
        order: 1,
        placeholder: 'Alias del control',
        validations: {
          validators: [Validators.required, Validators.pattern('^[a-zA-Z_]*$')],
          messages: { pattern: 'Caracteres invalidos: solo letras y guiones bajos' }
        }
      }),
      new Dropdown({
        key: 'cboTipoSelect',
        label: 'Tipo Selector:',
        value: control ?
          control.propiedades['tipoSelector'] ?
            control.propiedades['tipoSelector']
            : 'EnLista'
          : 'EnLista',
        options: [
          { key: 'EnLista', value: 'En lista' },
          { key: 'EnModal', value: 'En modal' }
        ],
        order: 2,
        validations: {
          validators: [Validators.required]
        },
      }),
      new Container({
        class: 'form-row',
        order: 3,
        controls: [
          new Textbox({
            key: 'chAgregar',
            type: 'checkbox',
            value: control ?
              control.propiedades['accionesInModal'] ?
                control.propiedades['accionesInModal']['agregarInModal'] ?
                  control.propiedades['accionesInModal']['agregarInModal']
                  : false
                : false
              : false,
            label: 'Agregar registro',
            size: 'col-12 col-md-3',
            condictions: [
              {
                campoDepende: 'cboTipoSelect',
                siEs: 'Igual',
                valor: 'EnModal',
                accion: 'Mostrar',
                operadorCondicion: 'Y'
              },
            ],
          }),
          new Textbox({
            key: 'chModificar',
            type: 'checkbox',
            value: control ?
              control.propiedades['accionesInModal'] ?
                control.propiedades['accionesInModal']['modificarInModal'] ?
                  control.propiedades['accionesInModal']['modificarInModal']
                  : false
                : false
              : false,
            label: 'Modificar registro',
            size: 'col-12 col-md-3',
            condictions: [
              {
                campoDepende: 'cboTipoSelect',
                siEs: 'Igual',
                valor: 'EnModal',
                accion: 'Mostrar',
                operadorCondicion: 'Y'
              },
            ],
          }),
          new Textbox({
            key: 'chEliminar',
            type: 'checkbox',
            value: control ?
              control.propiedades['accionesInModal'] ?
                control.propiedades['accionesInModal']['eliminarInModal'] ?
                  control.propiedades['accionesInModal']['eliminarInModal']
                  : false
                : false
              : false,
            label: 'Eliminar registro',
            size: 'col-12 col-md-3',
            condictions: [
              {
                campoDepende: 'cboTipoSelect',
                siEs: 'Igual',
                valor: 'EnModal',
                accion: 'Mostrar',
                operadorCondicion: 'Y'
              },
            ],
          }),
        ],
      }),
      new Dropdown({
        key: 'cboEspacios',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 4,
        options: this.setSizes(sizesControl),
        order: 4,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título del control:',
        value: control ? control.nombre : '',
        order: 5,
        placeholder: 'Título del control',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtPlaceholder',
        label: 'Placeholder del control:',
        value: control ? control.placeholder : '',
        order: 6,
        placeholder: 'Placeholder del control',
      }),
      new Textbox({
        key: "txtTooltip",
        label: "Tooltip del control:",
        value: control ? control.tooltip : "",
        order: 7,
        placeholder: "Tooltip del control",
      }),
      new Dropdown({
        key: 'cboMasterTable',
        label: 'Cargar información de:',
        options: this.setMasterTables(masterTables),
        value: control ?
          control.maestra_dependencia ?
            control.maestra_dependencia['id']
            : ''
          : '',
        order: 8,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Custom({
        key: 'selectColumn',
        label: 'Columnas a mostrar de la maestra',
        order: 9,
        template: templeteForm,
        placeholder: 'Columnas a mostrar de la maestra',
        value: control ?
          control.propiedades['columnas_seleccionadas'] ?
            control.propiedades['columnas_seleccionadas']
            : ''
          : '',
        options: optionsSelectColum,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Container({
        class: 'form-row',
        order: 10,
        controls: [
          new Textbox({
            key: 'chRequiered',
            type: 'checkbox',
            label: 'Requerido',
            size: 'col-12 col-md-4',
            value: control ? control.requerido : false,
          }),
          new Textbox({
            key: 'chOnly',
            type: 'checkbox',
            label: 'Unico',
            size: 'col-12 col-md-4',
            value: control ? control.unico : false,
          }),
          new Textbox({
            key: "chAutoAjustar",
            type: "checkbox",
            value: control ? (control.propiedades["auto_ajustar_tamano"] ? true : false) : false,
            label: "Auto ajustar tamaño del contenido",
            size: "col-12 col-md-3",
            condictions: [{
              campoDepende: "cboTipoSelect",
              siEs: "Igual",
              valor: "EnLista",
              accion: "Mostrar",
            },],
          }),
        ]
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

  /**
   * Crea el formulario para la creacion de los controles de tipo input-email
   *
   * @param {Array<EspacioControl>} sizesControl
   * @param {Control} [control]
   * @returns {BaseControl<any>[]}
   * @memberof CreationFormsFormService
   */
  getFormEmail(sizesControl: Array<EspacioControl>, control?: ColumnaMaestra): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'txtAlias',
        label: 'Alias del control:',
        value: control ? control.alias : '',
        describe: 'Nota: este alias se utiliza como referencia para poder acceder a cada campo del formulario ' +
          'y no debe llevar espacios ni caracteres especiales.',
        order: 1,
        placeholder: 'Alias del control',
        validations: {
          validators: [Validators.required, Validators.pattern('^[a-zA-Z_]*$')],
          messages: { pattern: 'Caracteres invalidos: solo letras y guiones bajos' }
        }
      }),
      new Dropdown({
        key: 'cboEspacios',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 4,
        options: this.setSizes(sizesControl),
        order: 2,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título del control:',
        value: control ? control.nombre : '',
        order: 3,
        placeholder: 'Título del control',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtPlaceholder',
        label: 'Placeholder del control:',
        value: control ? control.placeholder : '',
        order: 4,
        placeholder: 'Placeholder del control',
      }),
      new Textbox({
        key: "txtTooltip",
        label: "Tooltip del control:",
        value: control ? control.tooltip : "",
        order: 5,
        placeholder: "Tooltip del control",
      }),
      new Textbox({
        key: 'txtExpresion',
        label: 'Expresion regular:',
        value: control ? control.propiedades['expresion'] ? control.propiedades['expresion'] : '' : '',
        order: 6,
        placeholder: 'Expresion regular',
      }),
      new Textbox({
        key: 'txtErrorExp',
        label: 'Mensaje de error:',
        value: control ? control.propiedades['mensajeExpresion'] ? control.propiedades['mensajeExpresion'] : '' : '',
        order: 7,
        placeholder: 'Mensaje de error de la expresion regular',
      }),
      new Container({
        class: 'form-row',
        order: 8,
        controls: [
          new Textbox({
            key: 'chRequiered',
            type: 'checkbox',
            label: 'Requerido',
            size: 'col-12 col-md-4',
            value: control ? control.requerido : false,
          }),
          new Textbox({
            key: 'chOnly',
            type: 'checkbox',
            label: 'Unico',
            size: 'col-12 col-md-4',
            value: control ? control.unico : false,
          }),
        ]
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

  /**
   * Crea el formulario para la creacion de los controles de tipo input-number
   *
   * @param {Array<EspacioControl>} sizesControl
   * @param {Control} [control]
   * @returns {BaseControl<any>[]}
   * @memberof CreationFormsFormService
   */
  getFormNumber(sizesControl: Array<EspacioControl>, control?: ColumnaMaestra): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'txtAlias',
        label: 'Alias del control:',
        value: control ? control.alias : '',
        describe: 'Nota: este alias se utiliza como referencia para poder acceder a cada campo del formulario ' +
          'y no debe llevar espacios ni caracteres especiales.',
        order: 1,
        placeholder: 'Alias del control',
        validations: {
          validators: [Validators.required, Validators.pattern('^[a-zA-Z_]*$')],
          messages: { pattern: 'Caracteres invalidos: solo letras y guiones bajos' }
        }
      }),
      new Dropdown({
        key: 'cboEspacios',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 4,
        options: this.setSizes(sizesControl),
        order: 2,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título del control:',
        value: control ? control.nombre : '',
        order: 3,
        placeholder: 'Título del control',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtPlaceholder',
        label: 'Placeholder del control:',
        value: control ? control.placeholder : '',
        order: 4,
        placeholder: 'Placeholder del control',
      }),
      new Textbox({
        key: "txtTooltip",
        label: "Tooltip del control:",
        value: control ? control.tooltip : "",
        order: 5,
        placeholder: "Tooltip del control",
      }),
      new Textbox({
        key: 'txtMin',
        label: 'Valor mínimo:',
        value: control ? control.propiedades['min'] ? control.propiedades['min'] : null : null,
        type: 'number',
        order: 6,
        placeholder: 'Caracteres mínimos',
      }),
      new Textbox({
        key: 'chMayorQue',
        type: 'checkbox',
        value: control ? control.propiedades['mayorQue'] ? control.propiedades['mayorQue'] : false : false,
        label: 'Activar mayor Que',
        order: 7,
        condictions: [
          {
            campoDepende: 'txtMin',
            siEs: 'Vacio',
            accion: 'Ocultar'
          }
        ]
      }),
      new Textbox({
        key: 'txtMax',
        label: 'Valor máximo:',
        value: control ? control.propiedades['max'] ? control.propiedades['max'] : null : null,
        type: 'number',
        order: 8,
        placeholder: 'Caracteres máximos',
      }),
      new Textbox({
        key: 'chMenorQue',
        type: 'checkbox',
        value: control ? control.propiedades['menorQue'] ? control.propiedades['menorQue'] : false : false,
        label: 'Activar menor Que',
        order: 9,
        condictions: [
          {
            campoDepende: 'txtMax',
            siEs: 'Vacio',
            accion: 'Ocultar'
          }
        ]
      }),
      new Textbox({
        key: 'txtCantidadDecimales',
        label: 'Cantidad de decimales:',
        value: control ?
          control.propiedades['cantidadDecimales'] ?
            control.propiedades['cantidadDecimales']
            : null
          : null,
        type: 'number',
        order: 10,
        placeholder: 'Valor máximo',
        condictions: [
          {
            campoDepende: 'chEnteros',
            siEs: 'Igual',
            valor: true,
            accion: 'Ocultar'
          }
        ]
      }),
      new Textbox({
        key: 'txtExpresion',
        label: 'Expresion regular:',
        value: control ? control.propiedades['expresion'] ? control.propiedades['expresion'] : '' : '',
        order: 11,
        placeholder: 'Expresion regular',
      }),
      new Textbox({
        key: 'txtErrorExp',
        label: 'Mensaje de error:',
        value: control ? control.propiedades['mensajeExpresion'] ? control.propiedades['mensajeExpresion'] : '' : '',
        order: 12,
        placeholder: 'Mensaje de error de la expresion regular',
      }),
      new Textbox({
        key: 'chEnteros',
        type: 'checkbox',
        label: 'Numeros enteros',
        value: control ? control.propiedades['enteros'] ? control.propiedades['enteros'] : false : false,
        order: 13
      }),
      new Container({
        class: 'form-row',
        order: 14,
        controls: [
          new Textbox({
            key: 'chRequiered',
            type: 'checkbox',
            label: 'Requerido',
            size: 'col-12 col-md-4',
            value: control ? control.requerido : false,
          }),
          new Textbox({
            key: 'chOnly',
            type: 'checkbox',
            label: 'Unico',
            size: 'col-12 col-md-4',
            value: control ? control.unico : false,
          }),
        ]
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

  /**
   * Crea el formulario para la creacion de los controles de tipo label
   *
   * @param {Array<EspacioControl>} sizesControl
   * @param {Control} [control]
   * @returns {BaseControl<any>[]}
   * @memberof CreationFormsFormService
   */
  getFormLabel(sizesControl: Array<EspacioControl>, control?: ColumnaMaestra): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Dropdown({
        key: 'cboEspacios',
        label: 'Tamaño del control:',
        value: control ? control.espacio : 4,
        options: this.setSizes(sizesControl),
        order: 2,
        validations: {
          validators: [Validators.required]
        }
      }),
      new Textbox({
        key: 'txtLabel',
        label: 'Título:',
        value: control ? control.nombre : '',
        order: 3,
        placeholder: 'Título',
        validations: {
          validators: [Validators.required]
        }
      }),
      new Dropdown({
        key: 'cboSize',
        label: 'Tamaño de fuente:',
        options: [{ key: '2', value: 'Grande' }, { key: '4', value: 'Mediano' }, { key: '6', value: 'Pequeño' }],
        value: control ? control.propiedades['sizeFont'] ? control.propiedades['sizeFont'] : '2' : '2',
        order: 5,
        validations: {
          validators: [Validators.required]
        }
      }),
    ];

    return controls.sort((a, b) => a.order - b.order);
  }

}



// WEBPACK FOOTER //
// ./src/app/administration/teacher/master-structure/shared/master-structure-form.service.ts